import {
  createMenuItem,
  deleteMenuItem,
  getMenu,
  listPuckPages,
  reorderMenu,
  setMenuItemLabels,
  updateMenuItem,
  type NavMenu,
  type NavMenuItem,
  type NavMenuKey,
  type PuckPageListItem,
  type UpsertNavMenuItemInput,
} from "@/api/cms-puck/api";
import { MultilingualSelect } from "@/components/form-items/MultilingualField";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button.tsx";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useCmsWebsiteLanguages } from "@/hooks/use-cms-website-languages";
import { useToast } from "@/hooks/use-toast";
import type { SupportedLocale } from "@/i18n";
import { CmsAdminShell } from "@/pages/cms/advanced/AdminShell";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ChevronDown,
  ChevronUp,
  CornerDownRight,
  ExternalLink,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react";
import { useMemo, useState } from "react";
import { cmsNew, useCmsNew, type CmsNewKey } from "@/lib/cms/cms-new-i18n";
import { cmsPhrase } from "@/lib/cms/cms-new-phrases";

/**
 * Éditeur des menus de navigation (CMS avancé Puck) — route `/cms/advanced/menus`.
 * Gère les deux menus `primary` (header) et `footer`. Par menu : liste ordonnée
 * des items, ajout (page CMS avancé ou lien personnalisé), réordonnancement (haut/bas),
 * imbrication sur **4 niveaux** (« sous-menu de »), édition (cible / nouvel
 * onglet / label par langue) et suppression. Additif : ne touche en rien
 * l'ancien CMS ; le front public retombe sur l'ancienne nav tant qu'aucun menu
 * n'est résolu.
 *
 * Le MODÈLE (NavMenuItem.parentId auto-référencé) et l'API (résolveur public,
 * create/update/reorder) supportaient déjà une profondeur arbitraire — seul
 * l'ÉDITEUR était figé à 1 niveau. Le plafond de 4 niveaux est une limite
 * D'ÉDITION uniquement (cf. `MAX_DEPTH_INDEX`), pas une contrainte du modèle.
 */

const MENU_KEYS_BASE: Array<{
  key: NavMenuKey;
  labelKey: CmsNewKey;
  fallback: string;
}> = [
  {
    key: "primary",
    labelKey: "cms.new.auto.label.header_principal",
    fallback: "Header (principal)",
  },
  { key: "footer", labelKey: "cms.new.auto.label.footer", fallback: "Footer" },
];

/** Profondeur maximale éditable (0 = racine → 4 niveaux = index 0..3). */
const MAX_DEPTH_INDEX = 3;

/** Profondeur d'un item (0 = racine), en remontant `parentId`. */
function depthOf(id: string, byId: Map<string, NavMenuItem>): number {
  let depth = 0;
  const seen = new Set<string>();
  let current = byId.get(id);
  while (current?.parentId && !seen.has(current.id)) {
    seen.add(current.id);
    current = byId.get(current.parentId);
    depth += 1;
  }
  return depth;
}

/** Hauteur du sous-arbre d'un item (0 = feuille) — combien de niveaux en dessous. */
function subtreeHeight(id: string, items: NavMenuItem[]): number {
  const children = items.filter((i) => i.parentId === id);
  if (children.length === 0) return 0;
  return 1 + Math.max(...children.map((c) => subtreeHeight(c.id, items)));
}

/** `candidateId` est-il `ancestorId` lui-même ou l'un de ses descendants ? */
function isSelfOrDescendant(
  candidateId: string,
  ancestorId: string,
  byId: Map<string, NavMenuItem>,
): boolean {
  const seen = new Set<string>();
  let current = byId.get(candidateId);
  while (current) {
    if (current.id === ancestorId) return true;
    if (seen.has(current.id)) break;
    seen.add(current.id);
    current = current.parentId ? byId.get(current.parentId) : undefined;
  }
  return false;
}

/**
 * Ordonne les items en **profondeur d'abord** (parcours identique à `MenuTree`) :
 * chaque parent est immédiatement suivi de ses propres enfants, frères triés par
 * `position`. C'est ce qui permet au sélecteur de parent d'afficher les
 * sous-menus SOUS le menu auquel ils sont rattachés — même hiérarchie que la
 * liste de la page — plutôt qu'un tri à plat par niveau (tous les niveaux 0,
 * puis tous les niveaux 1…).
 */
function orderItemsDepthFirst(items: NavMenuItem[]): NavMenuItem[] {
  const childrenByParent = new Map<string | null, NavMenuItem[]>();
  for (const item of items) {
    const key = item.parentId ?? null;
    const bucket = childrenByParent.get(key) ?? [];
    bucket.push(item);
    childrenByParent.set(key, bucket);
  }
  for (const bucket of childrenByParent.values()) {
    bucket.sort((a, b) => a.position - b.position);
  }

  const ordered: NavMenuItem[] = [];
  const seen = new Set<string>();
  const walk = (parentId: string | null) => {
    for (const item of childrenByParent.get(parentId) ?? []) {
      if (seen.has(item.id)) continue; // garde-fou anti-cycle
      seen.add(item.id);
      ordered.push(item);
      walk(item.id);
    }
  };
  walk(null);
  return ordered;
}

const menuKey = (key: NavMenuKey) => ["cms-puck", "menu", key] as const;
const PAGES_KEY = ["cms-puck", "pages"] as const;

const CMSAdvancedMenus = () => {
  const [activeKey, setActiveKey] = useState<NavMenuKey>("primary");
  const cmsNew = useCmsNew();
  const menuKeys = useMemo(
    () =>
      MENU_KEYS_BASE.map((m) => ({
        key: m.key,
        label: cmsNew(m.labelKey, m.fallback),
      })),
    [cmsNew],
  );

  return (
    <CmsAdminShell
      title={cmsNew("cms.new.auto.title.menus", "Menus")}
      subtitle={cmsNew("cms.new.auto.subtitle.navigation_du_site_menu_principal_header_et_menu_du_footer", "Navigation du site : menu principal (header) et menu du footer.")}
    >
      <div className="px-6 py-4">
        <Tabs
          value={activeKey}
          onValueChange={(v) => setActiveKey(v as NavMenuKey)}
        >
          <TabsList>
            {menuKeys.map((m) => (
              // Fond transparent à l'état actif (pas de blanc plaqué sur le
              // fond gris clair de la page).
              <TabsTrigger
                key={m.key}
                value={m.key}
                className="data-[state=active]:bg-transparent"
              >
                {m.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <div className="mt-4">
          <MenuEditor menuKey={activeKey} />
        </div>
      </div>
    </CmsAdminShell>
  );
};

function MenuEditor({ menuKey: key }: { menuKey: NavMenuKey }) {
  const cmsNew = useCmsNew();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const menuQuery = useQuery({
    queryKey: menuKey(key),
    queryFn: () => getMenu(key),
  });
  const pagesQuery = useQuery({ queryKey: PAGES_KEY, queryFn: () => listPuckPages() });

  const [addOpen, setAddOpen] = useState(false);
  const [editItem, setEditItem] = useState<NavMenuItem | null>(null);
  const [deleteItem, setDeleteItem] = useState<NavMenuItem | null>(null);

  const setMenu = (data: NavMenu) => {
    queryClient.setQueryData(menuKey(key), data);
    void queryClient.invalidateQueries({ queryKey: ["cms-puck", "menu-resolve"] });
  };

  const reorderMutation = useMutation({
    mutationFn: (
      items: { id: string; parentId?: string | null; position: number }[],
    ) => reorderMenu(key, items),
    onSuccess: setMenu,
    onError: () =>
      toast({ title: cmsNew("cms.new.auto.title.echec_du_reordonnancement", "Échec du réordonnancement"), variant: "error" }),
  });

  const deleteMutation = useMutation({
    mutationFn: (itemId: string) => deleteMenuItem(itemId),
    onSuccess: (data) => {
      setMenu(data);
      setDeleteItem(null);
      toast({ title: cmsNew("cms.new.auto.title.item_supprime", "Item supprimé"), variant: "success", duration: 1400 });
    },
    onError: () => toast({ title: cmsNew("cms.new.auto.title.echec_de_la_suppression", "Échec de la suppression"), variant: "error" }),
  });

  const items = menuQuery.data?.items ?? [];
  const pages = pagesQuery.data ?? [];
  const pageById = useMemo(
    () => new Map<string, PuckPageListItem>(pages.map((p) => [p.id, p])),
    [pages],
  );

  /** Déplace un item parmi ses frères (même parent) vers le haut / le bas. */
  const move = (item: NavMenuItem, dir: -1 | 1) => {
    const siblings = items
      .filter((i) => i.parentId === item.parentId)
      .sort((a, b) => a.position - b.position);
    const idx = siblings.findIndex((s) => s.id === item.id);
    const swapIdx = idx + dir;
    if (swapIdx < 0 || swapIdx >= siblings.length) return;
    const reordered = [...siblings];
    [reordered[idx], reordered[swapIdx]] = [
      reordered[swapIdx],
      reordered[idx],
    ];
    reorderMutation.mutate(
      reordered.map((s, i) => ({
        id: s.id,
        parentId: s.parentId,
        position: i,
      })),
    );
  };

  if (menuQuery.isLoading) {
    return (
      <p className="py-12 text-center text-sm text-slate-400">{cmsNew("cms.new.auto.text.chargement", "Chargement…")}</p>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">
          {items.length === 0
            ? cmsNew("cms.new.menus.emptyList", "Aucun élément dans ce menu.")
            : `${items.length} ${cmsNew("cms.new.menus.itemsCountSuffix", "élément(s).")}`}
        </p>
        <Button size="sm" onClick={() => setAddOpen(true)}>
          <Plus size={14} className="mr-1" />
          {cmsNew("cms.new.menus.addItem", "Ajouter un élément")}
        </Button>
      </div>

      {items.length > 0 && (
        <div className="rounded-md border border-slate-200">
          <MenuTree
            items={items}
            parentId={null}
            depth={0}
            pageById={pageById}
            onMove={move}
            onEdit={setEditItem}
            onDelete={setDeleteItem}
          />
        </div>
      )}

      {addOpen && (
        <ItemDialog
          menuKey={key}
          pages={pages}
          items={items}
          onClose={() => setAddOpen(false)}
          onSaved={(data) => {
            setMenu(data);
            setAddOpen(false);
          }}
        />
      )}

      {editItem && (
        <ItemDialog
          menuKey={key}
          pages={pages}
          items={items}
          existing={editItem}
          onClose={() => setEditItem(null)}
          onSaved={(data) => {
            setMenu(data);
            setEditItem(null);
          }}
        />
      )}

      <Dialog
        open={!!deleteItem}
        onOpenChange={(open) => {
          if (!open) setDeleteItem(null);
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{cmsNew("cms.new.auto.text.supprimer_lelement", "Supprimer l'élément")}</DialogTitle>
            <DialogDescription>
              {cmsNew(
                "cms.new.menus.deleteItemDescription",
                "Cette action est définitive et supprime aussi ses éventuels sous-éléments.",
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteItem(null)}>
              {cmsPhrase(cmsNew, "cancel")}
            </Button>
            <Button
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={() =>
                deleteItem && deleteMutation.mutate(deleteItem.id)
              }
            >
              {cmsPhrase(cmsNew, "delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * Rendu RÉCURSIF de l'arbre du menu : affiche les frères de `parentId` (triés
 * par position) puis, sous chacun, ses propres enfants (profondeur+1) — jusqu'à
 * 4 niveaux de profondeur (`MAX_DEPTH_INDEX`). `onMove` compare toujours les
 * frères de MÊME parent, quel que soit le niveau (générique par construction).
 */
function MenuTree({
  items,
  parentId,
  depth,
  pageById,
  onMove,
  onEdit,
  onDelete,
}: {
  items: NavMenuItem[];
  parentId: string | null;
  depth: number;
  pageById: Map<string, PuckPageListItem>;
  onMove: (item: NavMenuItem, dir: -1 | 1) => void;
  onEdit: (item: NavMenuItem) => void;
  onDelete: (item: NavMenuItem) => void;
}) {
  const siblings = items
    .filter((i) => i.parentId === parentId)
    .sort((a, b) => a.position - b.position);

  return (
    <>
      {siblings.map((item, index) => (
        <div key={item.id}>
          <ItemRow
            item={item}
            pageById={pageById}
            depth={depth}
            isFirst={index === 0}
            isLast={index === siblings.length - 1}
            onUp={() => onMove(item, -1)}
            onDown={() => onMove(item, 1)}
            onEdit={() => onEdit(item)}
            onDelete={() => onDelete(item)}
          />
          <MenuTree
            items={items}
            parentId={item.id}
            depth={depth + 1}
            pageById={pageById}
            onMove={onMove}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        </div>
      ))}
    </>
  );
}

function ItemRow({
  item,
  pageById,
  depth,
  isFirst,
  isLast,
  onUp,
  onDown,
  onEdit,
  onDelete,
}: {
  item: NavMenuItem;
  pageById: Map<string, PuckPageListItem>;
  /** Profondeur dans l'arbre (0 = premier niveau). Pilote l'indentation. */
  depth: number;
  isFirst: boolean;
  isLast: boolean;
  onUp: () => void;
  onDown: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const { defaultWebsiteLanguage } = useCmsWebsiteLanguages();
  const page = item.pageId ? pageById.get(item.pageId) : undefined;
  const label =
    item.labels[defaultWebsiteLanguage] ??
    Object.values(item.labels)[0] ??
    (item.targetType === "PAGE"
      ? page?.name || cmsNew("cms.new.menus.pageTitleFallback", "(titre de page)")
      : item.url) ??
    cmsNew("cms.new.menus.noLabelFallback", "(sans libellé)");
  const badgeLine =
    item.targetType === "PAGE"
      ? `${cmsNew("cms.new.menus.badgePage", "Page")} : ${
          page?.name ?? item.pageId ?? "?"
        }`
      : `${cmsNew("cms.new.menus.badgeLink", "Lien")} : ${item.url}`;

  return (
    <div
      className={`flex items-center gap-2 border-b border-slate-100 px-3 py-2 last:border-b-0 ${
        depth > 0 ? "bg-slate-50/60" : ""
      }`}
      style={depth > 0 ? { paddingLeft: 12 + depth * 24 } : undefined}
    >
      {depth > 0 && (
        <CornerDownRight size={14} className="shrink-0 text-slate-300" />
      )}
      <div className="flex min-w-0 flex-1 flex-col">
        <span
          className="truncate text-sm font-medium text-slate-700"
          title={label}
        >
          {label}
          {item.openInNew && (
            <ExternalLink size={12} className="ml-1 inline text-slate-400" />
          )}
        </span>
        <span className="truncate text-xs text-slate-400" title={badgeLine}>
          {badgeLine}
        </span>
      </div>
      <Badge variant={item.targetType === "PAGE" ? "default" : "secondary"}>
        {item.targetType === "PAGE"
          ? cmsNew("cms.new.menus.badgePage", "Page")
          : cmsNew("cms.new.menus.badgeLink", "Lien")}
      </Badge>
      <div className="flex items-center gap-0.5">
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          disabled={isFirst}
          onClick={onUp}
          title={cmsNew("cms.new.auto.title.monter", "Monter")}
        >
          <ChevronUp size={15} />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          disabled={isLast}
          onClick={onDown}
          title={cmsNew("cms.new.auto.title.descendre", "Descendre")}
        >
          <ChevronDown size={15} />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={onEdit}
          title={cmsNew("cms.new.auto.title.editer", "Éditer")}
        >
          <Pencil size={14} />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-red-600"
          onClick={onDelete}
          title={cmsNew("cms.new.auto.title.supprimer", "Supprimer")}
        >
          <Trash2 size={14} />
        </Button>
      </div>
    </div>
  );
}

/** Dialogue d'ajout / édition d'un item (cible + nouvel onglet + parent + labels). */
function ItemDialog({
  menuKey: key,
  pages,
  items,
  existing,
  onClose,
  onSaved,
}: {
  menuKey: NavMenuKey;
  pages: PuckPageListItem[];
  /** Tous les items du menu, à plat (sert au calcul du sélecteur de parent). */
  items: NavMenuItem[];
  existing?: NavMenuItem;
  onClose: () => void;
  onSaved: (menu: NavMenu) => void;
}) {
  const cmsNew = useCmsNew();
  const { toast } = useToast();
  const { websiteLanguages, defaultWebsiteLanguage } = useCmsWebsiteLanguages();

  const [targetType, setTargetType] = useState<"PAGE" | "CUSTOM">(
    existing?.targetType ?? "PAGE",
  );
  const [pageId, setPageId] = useState<string>(existing?.pageId ?? "");
  const [url, setUrl] = useState<string>(existing?.url ?? "");
  const [openInNew, setOpenInNew] = useState<boolean>(
    existing?.openInNew ?? false,
  );
  const [parentId, setParentId] = useState<string>(existing?.parentId ?? "");
  const [labels, setLabels] = useState<
    Partial<Record<SupportedLocale, string>>
  >(existing?.labels ?? {});
  const [labelLang, setLabelLang] = useState<SupportedLocale>(
    defaultWebsiteLanguage,
  );

  const byId = useMemo(
    () => new Map(items.map((i) => [i.id, i])),
    [items],
  );
  // Hauteur du sous-arbre de l'item édité (0 pour un nouvel item / une feuille) :
  // le déplacer entraîne tout ce sous-arbre, donc la profondeur du NOUVEAU
  // parent doit laisser assez de place pour ces `height` niveaux en dessous.
  const height = existing ? subtreeHeight(existing.id, items) : 0;
  // Parents possibles (jusqu'à 4 niveaux) : ni soi-même, ni un de ses propres
  // descendants (cycle), et assez de marge sous le plafond pour tout le
  // sous-arbre déplacé.
  // Ordre depth-first (parent puis ses enfants), PUIS filtrage des parents
  // valides : les sous-menus restent contigus à leur parent dans le sélecteur.
  const possibleParents = useMemo(
    () =>
      orderItemsDepthFirst(items).filter((p) => {
        if (existing && isSelfOrDescendant(p.id, existing.id, byId)) {
          return false;
        }
        return depthOf(p.id, byId) + 1 + height <= MAX_DEPTH_INDEX;
      }),
    [items, byId, existing, height],
  );
  const pageById = useMemo(
    () => new Map(pages.map((p) => [p.id, p])),
    [pages],
  );
  // Libellé d'un parent dans le sélecteur : même logique de repli que `ItemRow`
  // (liste principale). Un item ciblant une page SANS libellé saisi doit
  // afficher le NOM de la page, pas le fallback générique « (item) ».
  const parentLabel = (p: NavMenuItem) =>
    p.labels[defaultWebsiteLanguage] ??
    Object.values(p.labels)[0] ??
    (p.targetType === "PAGE"
      ? (p.pageId ? pageById.get(p.pageId)?.name : undefined) ||
        cmsNew("cms.new.menus.pageTitleFallback", "(titre de page)")
      : p.url) ??
    cmsNew("cms.new.menus.parentFallback", "(élément)");

  const saveMutation = useMutation({
    mutationFn: async (): Promise<NavMenu> => {
      const input: UpsertNavMenuItemInput = {
        targetType,
        pageId: targetType === "PAGE" ? pageId : null,
        url: targetType === "CUSTOM" ? url.trim() : null,
        parentId: parentId || null,
        openInNew,
      };
      const menu = existing
        ? await updateMenuItem(existing.id, input)
        : await createMenuItem(key, input);
      // L'id de l'item nouvellement créé = le dernier ajouté à ce parent.
      const itemId =
        existing?.id ??
        menu.items
          .filter((i) => (i.parentId ?? "") === (input.parentId ?? ""))
          .sort((a, b) => b.position - a.position)[0]?.id;
      // Labels : on n'envoie que si au moins une valeur est saisie (ou édition).
      const hasLabels = Object.values(labels).some((v) => (v ?? "").trim());
      if (itemId && (hasLabels || existing)) {
        await setMenuItemLabels(itemId, labels);
        return getMenu(key);
      }
      return menu;
    },
    onSuccess: onSaved,
    onError: (err: unknown) => {
      const status = (err as { response?: { status?: number } })?.response
        ?.status;
      toast({
        title:
          status === 400
            ? cmsNew("cms.new.menus.targetInvalid", "Cible invalide (page ou URL manquante)")
            : cmsNew("cms.new.menus.saveFailed", "Échec de l'enregistrement"),
        variant: "error",
        duration: 3500,
      });
    },
  });

  const canSave =
    targetType === "PAGE" ? !!pageId : url.trim().length > 0;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {existing ? cmsPhrase(cmsNew, "menuEditItem") : cmsPhrase(cmsNew, "menuNewItem")}
          </DialogTitle>
          <DialogDescription>
            {cmsPhrase(cmsNew, "menuItemDescription")}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {/* Type de cible */}
          <RadioGroup
            value={targetType}
            onValueChange={(v) => setTargetType(v as "PAGE" | "CUSTOM")}
            className="flex gap-6"
          >
            <div className="flex items-center gap-2">
              <RadioGroupItem value="PAGE" id="t-page" />
              <Label htmlFor="t-page">{cmsNew("cms.new.auto.text.page_du_site", "Page du site")}</Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="CUSTOM" id="t-custom" />
              <Label htmlFor="t-custom">{cmsNew("cms.new.auto.text.lien_personnalise", "Lien personnalisé")}</Label>
            </div>
          </RadioGroup>

          {targetType === "PAGE" ? (
            <div className="flex flex-col gap-1.5">
              <Label>{cmsNew("cms.new.auto.text.page_cible", "Page cible")}</Label>
              <Select value={pageId} onValueChange={setPageId}>
                <SelectTrigger>
                  <SelectValue placeholder={cmsNew("cms.new.auto.placeholder.choisir_une_page", "Choisir une page…")} />
                </SelectTrigger>
                <SelectContent>
                  {pages.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name || cmsPhrase(cmsNew, "unnamedPage")}
                      {p.status !== "PUBLISHED" ? cmsNew("cms.new.menus.draftSuffix", " — brouillon") : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-400">
                {cmsNew("cms.new.menus.unpublishedPagesHint", "Les pages non publiées n'apparaîtront pas sur le site tant qu'elles ne sont pas publiées.")}
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="item-url">URL</Label>
              <Input
                id="item-url"
                value={url}
                placeholder={cmsNew("cms.new.auto.placeholder.https_ou_chemin", "https://… ou /chemin")}
                onChange={(e) => setUrl(e.target.value)}
              />
            </div>
          )}

          {/* Libellé par langue */}
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between">
              <Label>{cmsNew("cms.new.auto.text.libelle", "Libellé")}</Label>
              <MultilingualSelect
                values={websiteLanguages}
                value={labelLang}
                onValueChange={(v) => setLabelLang(v as SupportedLocale)}
              />
            </div>
            <Input
              value={labels[labelLang] ?? ""}
              placeholder={
                targetType === "PAGE"
                  ? cmsPhrase(cmsNew, "menuLabelPlaceholderPage")
                  : cmsPhrase(cmsNew, "menuLabelPlaceholderCustom")
              }
              onChange={(e) =>
                setLabels((prev) => ({ ...prev, [labelLang]: e.target.value }))
              }
            />
          </div>

          {/* Imbrication (jusqu'à 4 niveaux) */}
          <div className="flex flex-col gap-1.5">
            <Label>{cmsPhrase(cmsNew, "menuSubmenuParent")}</Label>
            <Select
              value={parentId || "none"}
              onValueChange={(v) => setParentId(v === "none" ? "" : v)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{cmsNew("cms.new.auto.text.element_de_premier_niveau", "— Élément de premier niveau")}</SelectItem>
                {possibleParents.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {"—".repeat(depthOf(p.id, byId))} {parentLabel(p)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-400">
              {cmsPhrase(cmsNew, "menuMaxNesting")}
              {possibleParents.length === 0
                ? cmsNew(
                    "cms.new.menus.noParentSlot",
                    " Aucun emplacement disponible en dessous du plafond.",
                  )
                : ""}
            </p>
          </div>

          {/* Nouvel onglet */}
          <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2">
            <Label htmlFor="open-new" className="cursor-pointer">
              {cmsPhrase(cmsNew, "openInNewTab")}
            </Label>
            <Switch
              id="open-new"
              checked={openInNew}
              onCheckedChange={setOpenInNew}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {cmsPhrase(cmsNew, "cancel")}
          </Button>
          <Button
            disabled={!canSave || saveMutation.isPending}
            onClick={() => saveMutation.mutate()}
          >
            {existing
              ? cmsNew("cms.new.menus.save", "Enregistrer")
              : cmsNew("cms.new.menus.add", "Ajouter")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default CMSAdvancedMenus;
